import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { shallow } from 'zustand/shallow'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n/config'
import { useApiErrorMessage } from '../../i18n/useApiErrorMessage'
// import { useHasPermissions, useHasRoles } from "react-simple-rbac";

import SearchBar from 'material-ui-search-bar'

import Box from '@mui/material/Box'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'

import Layout from '../Layout/Layout'
import DataTable from '../table/DataTable'

import { MaterialIcons } from '../../icons/Material/MaterialIcons'

import { useDrawerStore } from '../../store/drawerStore'
import { useEmpresaStore } from '../../store/empresaStore'
import { useUserStore } from '../../store/userStore'

import { EmpresasRowsType } from '../../types'
import HtmlTooltip from '../HtmlTooltip/HtmlTooltip'

//1: success, 2:pending, 3:cancel
const getAction = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        placeItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
      }}
    >
      <MaterialIcons.EditIcon />
    </Box>
  )
}

//0: active, 1:inactive
const getStatus = (params: GridRenderCellParams) => {
  if (params.value === 0) {
    return i18n.t('status.active', { ns: 'empresas' })
  }

  if (params.value === 1) {
    return i18n.t('status.inactive', { ns: 'empresas' })
  }
}

const Empresas = () => {
  const { t } = useTranslation('empresas')
  const getApiErrorMessage = useApiErrorMessage()

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('list.columns.name'),
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'status',
      headerName: t('list.columns.status'),
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
      renderCell: getStatus,
    },
    {
      field: 'action',
      headerName: t('list.columns.action'),
      type: 'number',
      editable: false,
      headerClassName: 'theme--header',
      renderCell: getAction,
      flex: 1,
    },
  ]

  const [searched, setSearched] = useState<string>('')
  const [companies, setCompanies] = useState<EmpresasRowsType[]>([])
  const [originalCompanies, setOriginalCompanies] = useState<EmpresasRowsType[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  const { getAccessTokenSilently } = useAuth0()

  const returnEmpresas = useEmpresaStore(state => state.returnEmpresas, shallow)

  const firstLogin = useDrawerStore(state => state.firstLogin, shallow)
  const userRoles = useUserStore(state => state.user?.auth0Roles, shallow)

  const isAdmin = useUserStore(state => state.user?.role, shallow)
  const setFirstLogin = useDrawerStore(state => state.setFirstLogin)

  const getAllEmpresas = useEmpresaStore(state => state.getAllEmpresas)
  const returnUserEmpresas = useEmpresaStore(state => state.returnUserEmpresas, shallow)
  const { hasFetchError, errorMessage, errorCode } = useEmpresaStore(
    ({ hasFetchError, errorMessage, errorCode }) => ({
      hasFetchError,
      errorMessage,
      errorCode,
    }),
    shallow,
  )

  // const isAdminOrManager = useHasRoles(["admin", "manager"], true);
  // const userCanCreate = useHasPermissions(["create_company"]);
  const hasUserSomeAdminRol =
    userRoles?.includes('bdo gerente') || userRoles?.includes('bdo superadmin')
  const isUserBDOBasicNoAdmin = userRoles?.includes('bdo basic') && !hasUserSomeAdminRol
  const canCreate = isAdmin === 2

  const requestSearch = (searchedVal: string) => {
    const searchCompanies = [...originalCompanies]
    if (searchedVal.length > 2) {
      const filteredRows = searchCompanies.filter(company => {
        return company.name.toLowerCase().includes(searchedVal.toLowerCase())
      })
      setCompanies(filteredRows)
    } else {
      setCompanies(originalCompanies)
    }
  }

  const cancelSearch = () => {
    setSearched('')
    requestSearch(searched)
  }

  const addCompany = () => {
    navigate('/empresas/empresa/crear empresa')
  }

  const getCompanies = useCallback(async () => {
    setIsLoading(true)
    const token = await getAccessTokenSilently()
    if (isUserBDOBasicNoAdmin) {
      // Obtener solo las empresas del usuario si es un usuario básico sin admin
      const userCompaniesRaw = returnUserEmpresas().map(item => item.company)
      // Mapear CompanyType a EmpresasRowsType
      const userCompanies: EmpresasRowsType[] = userCompaniesRaw.map(company => ({
        id: company.id,
        name: company.name,
        city: '', // Si tienes el dato, reemplaza aquí
        status: company.status === 0 ? t('status.active') : t('status.inactive'),
        payroll: '', // Si tienes el dato, reemplaza aquí
        action: null, // Si tienes el dato, reemplaza aquí
      }))
      setCompanies(userCompanies)
      setOriginalCompanies(userCompanies)
    } else {
      // Obtener todas las empresas si no es un usuario básico sin admin
      await getAllEmpresas(token)
      const allCompanies = returnEmpresas()
      setCompanies(allCompanies)
      setOriginalCompanies(allCompanies)
    }

    if (firstLogin) {
      setFirstLogin(false)
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getCompanies()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout icon="HomeWorkOutlinedIcon" title={t('list.breadcrumb')}>
      <Box
        sx={{
          height: '100%',
          '& .theme--header': {
            backgroundColor: '#EEF1FC',
          },
        }}
      >
        <Grid container spacing={2} sx={{ py: '24px', placeItems: 'center' }}>
          <Grid display="flex" sx={{ placeItems: 'center' }} item xs={6}>
            <Box sx={{ marginRight: 4, width: '459px' }}>
              <SearchBar
                value={searched}
                onChange={(searchVal: string) => requestSearch(searchVal)}
                onCancelSearch={() => cancelSearch()}
                placeholder={t('list.searchPlaceholder')}
                style={{ boxShadow: 'none' }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Grid container display="flex" justifyContent="flex-end">
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<MaterialIcons.FilterAltIcon />}
                  sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    '&:hover': { color: 'white' },
                  }}
                >
                  {t('list.filterButton')}
                </Button>
                {canCreate ? (
                  <Button
                    id="add-company-button"
                    variant="contained"
                    startIcon={<MaterialIcons.HomeWorkOutlinedIcon />}
                    color="primary"
                    onClick={() => addCompany()}
                  >
                    {t('list.createButtonPlural')}
                  </Button>
                ) : (
                  <HtmlTooltip title={<b>{t('list.noCreatePermission')}</b>}>
                    <span>
                      <Button
                        id="add-company-disabled-button"
                        variant="contained"
                        startIcon={<MaterialIcons.HomeWorkOutlinedIcon />}
                        color="primary"
                        disabled
                        onClick={() => addCompany()}
                      >
                        {t('list.createButton')}
                      </Button>
                    </span>
                  </HtmlTooltip>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <DataTable
          columns={columns}
          rows={companies}
          navigateRowLink="/empresas/empresa"
          loading={isLoading}
          noFilterRowsMessage={t('list.noFilterRowsMessage')}
          noRowsMessage={
            hasFetchError
              ? getApiErrorMessage({ response: { data: { error_code: errorCode, error: errorMessage } } })
              : t('list.noRowsMessage')
          }
        />
      </Box>
    </Layout>
  )
}

export default Empresas
